import { ParsedUrlQuery } from 'querystring'
import { GetStaticPropsResult, GetStaticPathsResult } from 'next'
import dynamic from 'next/dynamic'

import { blogAuthorPageModuleQuery } from '@data/sanity/queries/page'
import { SanityBlogCategory } from '@data/sanity/queries/types/blog'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getAllLocales, getLocaleLanguage, Locale } from '@lib/language'
import { useBlogCanonicalUrl } from '@lib/metadata'
import {
  BasePageProps,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import {
  CustomStaticPropsContext,
  PageType,
  StaticPathWithSlug,
} from '@lib/routes'
import {
  getAllBlogSettings,
  getBlogAuthorPage,
  getBlogLocale,
} from '@lib/sanity/blog'
import { getSanityClient } from '@lib/sanity/client'
import { defaultModuleQueryParameters, getAllSlugs } from '@lib/sanity/page'

import Layout from '@components/layout'
import Modules from '@modules/modules'
const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type BlogAuthorPageProps = BasePageProps & {
  blogLocale: Locale
  slug: string
  page: SanityBlogCategory
  site: SanitySiteFragment
}

const BlogAuthorPage = ({
  draftMode,
  locale,
  blogLocale,
  slug,
  page,
  site,
}: BlogAuthorPageProps) => {
  const canonicalUrl = useBlogCanonicalUrl(
    PageType.BLOG_AUTHOR,
    page.slug,
    site.blogSettings,
  )

  return (
    <Layout
      page={page}
      site={site}
      canonicalUrl={canonicalUrl}
      draftMode={draftMode}
      className="bg-gray text-black"
    >
      {!draftMode && <Modules posts={page.posts} />}
      {draftMode && (
        <PreviewModules
          posts={page.posts}
          query={blogAuthorPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
            blogLocale,
            blogAuthorSlug: slug,
          }}
        />
      )}
    </Layout>
  )
}

interface BlogAuthorPageParams extends ParsedUrlQuery {
  slug: string
}

export async function getStaticProps(
  context: CustomStaticPropsContext<BlogAuthorPageParams>,
): Promise<GetStaticPropsResult<BlogAuthorPageProps>> {
  const locale = getLocaleLanguage(context.locale)

  // Sanity data
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const blogLocale = await getBlogLocale(sanityClient, locale)
  const slug = context.params?.slug ?? ''
  const blogAuthorPage = await getBlogAuthorPage(
    sanityClient,
    locale,
    blogLocale,
    slug,
  )

  if (!blogAuthorPage.page) {
    return {
      notFound: true,
      revalidate: 60,
    }
  }

  return {
    props: {
      ...getPagePropsFromContext(context),
      blogLocale: blogLocale as Locale,
      slug,
      page: blogAuthorPage.page,
      site: blogAuthorPage.site,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<BlogAuthorPageParams>
> {
  const sanityClient = getSanityClient()
  const blogAuthors = await getAllSlugs(sanityClient, 'blogAuthor')
  const siteLocales = getAllLocales()
  const blogSettings = await getAllBlogSettings(sanityClient, siteLocales)
  const paths: StaticPathWithSlug[] = []

  blogAuthors.forEach((blogAuthor) => {
    const locale = blogAuthor.locale as Locale

    if (blogSettings.hasLocaleContent[locale]) {
      // Add page locale path
      paths.push({
        params: {
          slug: blogAuthor.slug,
        },
        locale,
      })

      // Add inherited locale paths
      for (const childLocale of blogSettings.childLocales[locale]) {
        paths.push({
          params: {
            slug: blogAuthor.slug,
          },
          locale: childLocale,
        })
      }
    }
  })

  return {
    paths,
    fallback: 'blocking',
  }
}

export default BlogAuthorPage
